/**
 * Hook for managing UI language preference.
 * User picks 'source' (my language) or 'target' (language I'm learning).
 * Resolved to actual locale at runtime.
 */

import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  setUILanguage,
  getUILanguagePreference,
  getLanguageDisplayName,
  UI_LANGUAGE_PREFERENCES,
} from './index';
import type { UILanguagePreference } from '../types/locale';

export function useUILanguage() {
  const { i18n } = useTranslation();
  const [currentPreference, setCurrentPreference] = useState<UILanguagePreference>(
    getUILanguagePreference(),
  );

  // Sync state when language changes externally
  useEffect(() => {
    const handleLanguageChanged = () => {
      setCurrentPreference(getUILanguagePreference());
    };
    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  const changeLanguage = useCallback(async (pref: UILanguagePreference) => {
    await setUILanguage(pref);
    setCurrentPreference(pref);
  }, []);

  return {
    /** Current preference: 'source' or 'target' */
    currentPreference,
    /** Current resolved i18n language code (e.g., 'es-MX', 'en') */
    currentLanguage: i18n.language,
    /** Change the UI language preference */
    changeLanguage,
    /** Available preferences */
    supportedPreferences: UI_LANGUAGE_PREFERENCES,
    /** Get display label for a language code */
    getLanguageLabel: (lang: string) => getLanguageDisplayName(lang, i18n.language),
  };
}
